<!-- DeliveryInfoCard.vue -->
<template>
  <div class="delivery-tile info-card">
    <!-- Dialog pour les vigilances -->
    <Dialog 
      v-if="hasVigilances" 
      v-model:visible="showVigilanceDialog" 
      modal 
      :closable="isConfirmed"
      :style="{ width: '90%', maxWidth: '500px' }"
      :closeOnEscape="isConfirmed"
      :dismissableMask="isConfirmed">
      <template #header>
        <div class="dialog-header-content">
          <i class="material-icons warning-icon">warning</i>
          <span>{{ vigilancesHeaderText }}</span>
        </div>
      </template>
      <div class="vigilance-dialog-content">
        <div v-for="(vigilance, index) in delivery.vigilances" 
             :key="vigilance.id" 
             class="vigilance-alert mb-3">
          <div class="vigilance-number" v-if="delivery.vigilances.length > 1">{{ index + 1 }}</div>
          <div class="vigilance-content">
            <h6 class="vigilance-title">{{ vigilance.title }}</h6>
            <p class="vigilance-description">{{ vigilance.description }}</p>
          </div>
        </div>
        <div class="acknowledgement-section mt-4">
          <Checkbox v-model="isConfirmed" :binary="true" inputId="confirm-vigilance" />
          <label for="confirm-vigilance" class="ml-2">J'ai pris connaissance de ces points d'attention et je m'engage à les respecter</label>
        </div>
      </div>
      <template #footer>
        <Button 
          label="Fermer" 
          icon="pi pi-check" 
          :disabled="!isConfirmed" 
          @click="closeVigilanceDialog" 
          autofocus 
        />
      </template>
    </Dialog>

    <div class="delivery-tile-content">
      <div :class="['delivery-tile-header', statusClass]">
        <Badge :value="delivery.route_position"
               severity="secondary"
               class="route-position" />
        <h4 class="delivery-title text-white">
          {{ areNamesIdentical(delivery.deliveryLocation.name, delivery.organisation.name)
            ? delivery.deliveryLocation.name
            : `${delivery.deliveryLocation.name} - ${delivery.organisation.name}` }}
        </h4>
      </div>

      <div class="delivery-tile-body">
        <!-- Vigilances - Nouvelle section -->
        <div v-if="hasVigilances" class="vigilance-section">
          <div class="section-header alert-header">
            <span class="material-icons text-red-500">warning</span>
            <h6 class="section-title text-red-500">Point d'attention !</h6>
          </div>
          <div v-for="vigilance in delivery.vigilances" 
               :key="vigilance.id" 
               class="vigilance-alert">
            <h6 class="vigilance-title">{{ vigilance.title }}</h6>
            <p class="vigilance-description">{{ vigilance.description }}</p>
          </div>
        </div>

        <!-- Section adresse et contact sur la même ligne - mobile first -->
        <div class="info-row">
          <!-- Adresse -->
          <div class="info-col">
            <div class="section-header">
              <span :class="['material-icons', statusColorClass]">location_on</span>
              <h6 class="section-title">Adresse</h6>
              <button 
                class="copy-button" 
                type="button" 
                @click="copyAddressToClipboard" 
                title="Copier l'adresse"
              >
                <span class="material-icons copy-icon">content_copy</span>
              </button>
            </div>
            <p class="address-text">{{ formatAddress(delivery.deliveryLocation.address) }}</p>
          </div>

          <!-- Contact simplifié -->
          <div class="info-col">
            <div class="section-header">
              <span :class="['material-icons', statusColorClass]">person</span>
              <h6 class="section-title">Contact</h6>
            </div>
            <p class="info-text">
              {{ delivery.deliveryLocation.first_name }} 
              {{ delivery.deliveryLocation.last_name }}
            </p>
          </div>
        </div>

        <!-- Instructions de livraison -->
        <div class="instructions-section" v-if="delivery.deliveryLocation.delivery_instructions">
          <div class="section-header">
            <span :class="['material-icons', statusColorClass]">info</span>
            <h6 class="section-title">Instructions</h6>
          </div>
          <p class="info-text">{{ delivery.deliveryLocation.delivery_instructions }}</p>
        </div>

        <!-- Liste des colis -->
        <div class="packages-section">
          <div class="section-header">
            <span :class="['material-icons', statusColorClass]">inventory_2</span>
            <h6 class="section-title">Produits à livrer</h6>
          </div>
          <div class="package-container">
            <div 
              v-for="item in delivery.package_deliveries" 
              :key="item.id"
              class="package-badge">
              <Badge :value="`${item.quantity} × ${item.package.name}`"
                     :severity="getStatusSeverity(delivery.status)"
                     class="product-badge" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { defineProps } from 'vue'
import Card from 'primevue/card'
import Badge from 'primevue/badge'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import { useToast } from 'primevue/usetoast'

const props = defineProps({
  delivery: {
    type: Object,
    required: true
  }
})

const toast = useToast()
const showVigilanceDialog = ref(false)
const isConfirmed = ref(false)

// Computed property to check if there are vigilances
const hasVigilances = computed(() => {
  return props.delivery.vigilances && props.delivery.vigilances.length > 0
})

// Dynamic header text based on number of vigilances
const vigilancesHeaderText = computed(() => {
  const count = props.delivery.vigilances ? props.delivery.vigilances.length : 0;
  return count > 1 
    ? `${count} Points d'attention !` 
    : "Point d'attention !";
})

// Show the dialog when component is mounted if there are vigilances
onMounted(() => {
  if (hasVigilances.value) {
    showVigilanceDialog.value = true
  }
})

// Function to close the dialog
const closeVigilanceDialog = () => {
  if (isConfirmed.value) {
    showVigilanceDialog.value = false
  }
}

// Fonction pour copier l'adresse complète dans le presse-papier
const copyAddressToClipboard = () => {
  if (!props.delivery?.deliveryLocation) {
    return
  }
  
  // Récupérer l'adresse complète avec code postal et ville
  const fullAddress = `${props.delivery.deliveryLocation.address}, ${props.delivery.deliveryLocation.zip_code} ${props.delivery.deliveryLocation.city}`
  
  // Utilisation de l'API Clipboard pour copier dans le presse-papier
  navigator.clipboard.writeText(fullAddress)
    .then(() => {
      toast.add({
        severity: 'success',
        summary: 'Adresse copiée',
        detail: 'L\'adresse a été copiée dans le presse-papier',
        life: 2000
      })
    })
    .catch(err => {
      console.error('Erreur lors de la copie :', err)
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Impossible de copier l\'adresse',
        life: 2000
      })
    })
}

const formatAddress = (address) => {
  if (!address || typeof address !== 'string') return '';
  return address.replace(/, France$/, '')
}

const statusClass = computed(() => getStatusBackgroundClass(props.delivery.status))
const statusColorClass = computed(() => getStatusColorClass(props.delivery.status))

const getStatusBackgroundClass = (status) => {
  const backgroundMap = {
    'PLANNED': 'bg-blue-400',
    'DELIVERED': 'bg-green-400',
    'NOT DELIVERED': 'bg-red-400',
    'DO NOT DELIVER': 'bg-purple-400',
    'IN PROGRESS': 'bg-orange-400'
  }
  return backgroundMap[status] || 'bg-gray-600'
}

const areNamesIdentical = (name1, name2) => {
  if (!name1 || !name2) return false;
  return name1.toLowerCase() === name2.toLowerCase();
}

const getStatusColorClass = (status) => {
  const colorMap = {
    'PLANNED': 'text-blue-400',
    'DELIVERED': 'text-green-400',
    'NOT DELIVERED': 'text-red-400',
    'DO NOT DELIVER': 'text-purple-400',
    'IN PROGRESS': 'text-orange-400'
  }
  return colorMap[status] || 'text-gray-600'
}

const getStatusSeverity = (status) => {
  const severityMap = {
    'PLANNED': 'info',
    'DELIVERED': 'success',
    'NOT DELIVERED': 'danger',
    'DO NOT DELIVER': 'warning',
    'IN PROGRESS': 'warning'
  }
  return severityMap[status] || 'secondary'
}
</script>

<style scoped>
/* Import des icônes Material */
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.info-card {
  margin-bottom: 1rem;
}

/* Styles dérivés de MyTournee.vue */
.delivery-tile {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
  overflow: hidden;
  width: 100%;
}

.delivery-tile-content {
  display: flex;
  flex-direction: column;
}

.delivery-tile-header {
  display: flex;
  align-items: center;
  padding: 0.8rem 1rem;
  position: relative;
}

.route-position {
  font-size: 1rem;
  margin-right: 0.8rem;
}

.delivery-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  flex: 1;
}

.delivery-tile-body {
  padding: 0.8rem 1rem;
}

/* Styles pour la section vigilance */
.vigilance-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #FEF2F2;
  border: 1px solid #FCA5A5;
  border-radius: 6px;
}

.vigilance-alert {
  margin-top: 0.75rem;
}

.vigilance-title {
  font-weight: 600;
  font-size: 1rem;
  color: #DC2626;
  margin-bottom: 0.25rem;
}

.vigilance-description {
  font-size: 0.875rem;
  color: #7F1D1D;
}

.alert-header {
  border-bottom: 1px solid rgba(252, 165, 165, 0.3);
  padding-bottom: 0.5rem;
}

/* Info row layout - toujours en ligne */
.info-row {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  width: 100%;
}

.info-col {
  flex: 1;
  min-width: 0; /* Empêche le débordement sur mobile */
}

/* Headers communs */
.section-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
}

/* Icônes */
.material-icons {
  font-size: 1.25rem;
}

/* Texte */
.info-text {
  font-size: 0.9rem;
  line-height: 1.4;
  word-break: break-word;
  color: var(--text-color, #495057);
}

.address-text {
  font-size: 0.9rem;
  line-height: 1.4;
  word-break: break-word;
  color: var(--text-color, #495057);
}

/* Packages grid */
.package-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.package-badge {
  transition: transform 0.2s ease;
}

.package-badge:hover {
  animation: badge-pulse 0.5s;
}

.product-badge {
  background-color: #f1f5f9;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 1px solid #e2e8f0;
}

.package-badge:hover .product-badge {
  background-color: #e2e8f0;
  border-color: #cbd5e1;
}

/* Sections spacing */
.instructions-section,
.packages-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
}

/* Styles pour le bouton de copie */
.copy-button {
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border-radius: 50%;
  margin-left: auto;
  transition: background-color 0.2s;
}

.copy-button:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.copy-button:active {
  background-color: rgba(0, 0, 0, 0.1);
}

.copy-icon {
  font-size: 1rem !important;
  color: #666;
}

/* Tooltip & feedback */
.copy-button[title]:hover::after {
  content: attr(title);
  position: absolute;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-top: -1.5rem;
  white-space: nowrap;
  z-index: 10;
}

/* Animation pour les badges */
@keyframes badge-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Media queries pour les plus grands écrans */
@media (min-width: 640px) {
  .delivery-tile-header {
    padding: 1rem;
  }

  .info-row {
    gap: 1.25rem;
  }

  .delivery-title {
    font-size: 1.2rem;
  }

  .section-title {
    font-size: 1rem;
  }

  .info-text, .address-text {
    font-size: 1rem;
  }

  .material-icons {
    font-size: 1.5rem;
  }

  .package-container {
    gap: 0.75rem;
  }

  .product-badge {
    font-size: 0.9rem;
  }

  .vigilance-section {
    padding: 1.25rem;
  }

  .vigilance-title {
    font-size: 1.125rem;
  }

  .vigilance-description {
    font-size: 1rem;
  }
}

/* Styles pour le Dialog de vigilances */
.vigilance-dialog-content {
  padding: 0.5rem;
  background-color: #FEF2F2;
  border-radius: 6px;
}

.vigilance-dialog-content .vigilance-alert {
  padding: 0.75rem;
  border-bottom: 1px solid rgba(252, 165, 165, 0.3);
  display: flex;
  align-items: flex-start;
}

.vigilance-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #DC2626;
  color: white;
  font-weight: bold;
  font-size: 0.85rem;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.vigilance-content {
  flex: 1;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  color: white;
}

.warning-icon {
  margin-right: 0.5rem;
  color: white;
}

.vigilance-dialog-content .vigilance-alert:last-child {
  border-bottom: none;
}

.acknowledgement-section {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: white;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
}

:deep(.p-dialog-header) {
  background-color: #DC2626 !important;
  color: white !important;
}

:deep(.p-dialog-title) {
  font-weight: 600;
}

:deep(.p-checkbox-box.p-highlight) {
  background-color: #DC2626;
  border-color: #DC2626;
}

.mb-3 {
  margin-bottom: 0.75rem;
}

.mt-4 {
  margin-top: 1rem;
}

.ml-2 {
  margin-left: 0.5rem;
}
</style>